/**
 * firestoreReconnect — auto-reconnect wrapper for Firestore `onSnapshot`.
 *
 * Filed as relay-51 Part 2 (Cloud Run idle-close timeouts kill long-lived
 * Firestore streams; the UI then sits silent until manual refresh). The
 * server-side fix is the Cloud Run timeout bump (Part 1, Travis); this is
 * the defensive client-side counterpart.
 *
 * Caller pattern:
 *
 *   const unsub = subscribeWithReconnect(({ retry, markConnected }) =>
 *     onSnapshot(
 *       q,
 *       snap => { markConnected(); setX(snap); },
 *       err => { setError(err.message); retry(); }
 *     )
 *   );
 *
 * `markConnected()` resets the backoff once a snapshot has actually
 * arrived (so a listener that fails *after* connecting still gets a fast
 * first retry, while a listener that fails *during* setup backs off).
 *
 * `retry()` tears down the current subscription and re-subscribes after
 * an exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (capped).
 *
 * Visibility flip from hidden → visible during a backoff window triggers
 * an immediate retry — covers the "tab in the background dropped, no
 * point waiting 30s after the user comes back" case.
 *
 * cost-fix (finding #4 from seq #29994) — `retry()` optionally takes the
 * triggering error. A Firestore `permission-denied` error is permanent
 * (a rules/auth problem, not a network blip retrying would ever fix), so
 * it stops the loop entirely instead of backing off forever. Every other
 * error — including no error at all, for callers that don't pass one —
 * keeps the original always-retry behavior unchanged.
 */

type Unsubscribe = () => void;

interface Handlers {
  retry: (err?: unknown) => void;
  markConnected: () => void;
}

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

function isPermissionDenied(err: unknown): boolean {
  return typeof err === "object" && err !== null && (err as { code?: unknown }).code === "permission-denied";
}

export function subscribeWithReconnect(
  setup: (handlers: Handlers) => Unsubscribe,
): Unsubscribe {
  let cancelled = false;
  let currentUnsub: Unsubscribe | null = null;
  let backoffMs = INITIAL_BACKOFF_MS;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function teardown() {
    if (currentUnsub) {
      try { currentUnsub(); } catch { /* swallow — already torn down */ }
      currentUnsub = null;
    }
  }

  function markConnected() {
    backoffMs = INITIAL_BACKOFF_MS;
  }

  function stopPermanently() {
    cancelled = true;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    teardown();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", visibilityHandler);
    }
  }

  function retry(err?: unknown) {
    if (cancelled) return;
    if (isPermissionDenied(err)) {
      console.error("[firestoreReconnect] permission-denied is a permanent error — giving up retries:", err);
      stopPermanently();
      return;
    }
    teardown();
    if (retryTimer !== null) return;
    const delay = backoffMs;
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      doSetup();
    }, delay);
  }

  function doSetup() {
    if (cancelled) return;
    try {
      currentUnsub = setup({ retry, markConnected });
    } catch (err) {
      retry(err);
    }
  }

  function visibilityHandler() {
    if (cancelled) return;
    if (typeof document === "undefined") return;
    if (document.visibilityState === "visible" && retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
      doSetup();
    }
  }

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", visibilityHandler);
  }
  doSetup();

  return stopPermanently;
}
